import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  createQuestion,
  deleteQuestion,
  getAdminCategories,
  getAdminQuestion,
  getAdminQuestions,
  getPlatformColumns,
  getPlatformSuggestions,
  getQueryConfig,
  patchQuestionStatus,
  previewQuestion,
  updateQuestion,
  updateQueryConfig,
} from '../services/adminApi';
import DeleteConfirmModal from './DeleteConfirmModal';
import AddParameterModal from './AddParameterModal';

const MAX_TEMPLATE_LEN = 500;
const PLATFORMS = ['BASIC_PRODUCT', 'CHEMICAL_DIRECTORY', 'TECHNICAL_ROUTES'];
const OPERATORS = [
  { value: '=', label: 'Equals (=)' },
  { value: '!=', label: 'Not Equals (!=)' },
  { value: 'like', label: 'Contains (LIKE)' },
  { value: 'regex', label: 'Regex' },
  { value: '>', label: 'Greater Than (>)' },
  { value: '<', label: 'Less Than (<)' },
  { value: '>=', label: 'Greater or Equal (>=)' },
  { value: '<=', label: 'Less or Equal (<=)' },
  { value: 'in', label: 'In List (IN)' },
];

const EMPTY_FORM = {
  categoryId: '',
  questionTemplate: '',
  platform: '',
  displayOrder: '1',
  isRecommended: false,
  defaultParameters: {},
  editableFields: [],
  dbFieldMap: {},
  recommendedQuestionIds: [],
};

const isActiveStatus = (item) => item?.status === true || item?.status === 'active';

function useDebounce(value, delay) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return d;
}

function extractParams(template) {
  const matches = template.match(/\{(\w+)\}/g) || [];
  return [...new Set(matches.map((m) => m.slice(1, -1)))];
}

function previewRecords(data) {
  if (Array.isArray(data?.result)) return data.result;
  if (Array.isArray(data?.results)) return data.results;
  return data?.result && typeof data.result === 'object' ? [data.result] : [];
}

function previewValue(value) {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) return value.map((item) => previewValue(item)).join(', ');
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

const PREVIEW_VALUE_LIMIT = 420;
const IMAGE_URL = /\.(png|jpe?g|gif|webp|svg)(?:[?#].*)?$/i;
const FILE_URL = /\.(pdf|xlsx?|csv|docx?|pptx?)(?:[?#].*)?$/i;

function fileNameFromUrl(url) {
  try { return decodeURIComponent(new URL(url).pathname.split('/').pop() || 'Download file'); } catch { return 'Download file'; }
}

function PreviewResultValue({ value, onViewDetails }) {
  if (/^https?:\/\//i.test(value)) {
    if (IMAGE_URL.test(value)) {
      return <a className="admin-end-preview__image-link" href={value} target="_blank" rel="noreferrer"><img src={value} alt="Result" onError={(event) => { event.currentTarget.style.display = 'none'; }} /></a>;
    }
    if (FILE_URL.test(value)) {
      return <a className="admin-end-preview__file-link" href={value} target="_blank" rel="noreferrer" download><span>⇩</span> Download {fileNameFromUrl(value)}</a>;
    }
    return <a className="admin-end-preview__file-link" href={value} target="_blank" rel="noreferrer"><span>↗</span> Open link</a>;
  }
  const isLong = value.length > PREVIEW_VALUE_LIMIT;
  const visibleValue = isLong ? `${value.slice(0, PREVIEW_VALUE_LIMIT).trimEnd()}…` : value;
  return (
    <>
      <div className={`admin-end-preview__value${isLong ? ' is-truncated' : ''}`}>{visibleValue}</div>
      {isLong && <button type="button" className="admin-end-preview__details-btn" onClick={() => onViewDetails(value)}>View details</button>}
    </>
  );
}

function validate(form) {
  const e = {};
  if (!form.categoryId) e.categoryId = 'Category is required';
  if (!form.platform) e.platform = 'Platform is required';
  if (!form.questionTemplate.trim()) e.questionTemplate = 'Question template is required';
  else if (form.questionTemplate.trim().length < 5) e.questionTemplate = 'Template must be at least 5 characters';
  const parameters = extractParams(form.questionTemplate);
  if (parameters.some((parameter) => !form.dbFieldMap?.[parameter])) {
    e.dbFieldMap = 'Select a database field for every question parameter';
  }
  if (parameters.some((parameter) => !String(form.defaultParameters?.[parameter] || '').trim())) {
    e.defaultParameters = 'Enter a default value for every question parameter';
  }
  return e;
}

function Toggle({ checked, onChange, label }) {
  return (
    <button type="button" className={`admin-toggle ${checked ? 'is-on' : ''}`} onClick={() => onChange(!checked)} role="switch" aria-checked={checked}>
      <span className="admin-toggle__track"><span className="admin-toggle__thumb" /></span>
      {label !== false && <span className="admin-toggle__label">{checked ? 'Yes' : 'No'}</span>}
    </button>
  );
}

function SearchSelect({ value, onChange, options, placeholder, error }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    const handle = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target) && !(dropRef.current && dropRef.current.contains(e.target))) {
        setOpen(false); setSearch('');
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const handleOpen = () => setOpen((v) => !v);

  const selected = options.find((o) => o.value === value);
  const filtered = options.filter((o) => !search || o.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="admin-search-select" ref={wrapRef}>
      <button type="button" className={`admin-search-select__trigger${error ? ' is-error' : ''}`} onClick={handleOpen}>
        <span>{selected ? selected.label : (placeholder || 'Select')}</span>
        <svg viewBox="0 0 24 24" width="12" height="12"><path fill="currentColor" d="M7 10l5 5 5-5H7z" /></svg>
      </button>
      {open && (
        <div className="admin-search-select__dropdown" ref={dropRef}>
          <div className="admin-search-select__search-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
            <input type="text" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} autoFocus onClick={(e) => e.stopPropagation()} />
          </div>
          <ul>
            {filtered.length === 0 && <li className="admin-search-select__empty">No options found</li>}
            {filtered.map((o) => (
              <li key={o.value} className={o.value === value ? 'is-selected' : ''} onMouseDown={(e) => { e.preventDefault(); onChange(o.value); setOpen(false); setSearch(''); }}>
                {o.value === value && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><path d="M20 6L9 17l-5-5" /></svg>}
                {o.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RecommendedQuestionsSelect({ value, onChange, allQuestions, currentQuestionId }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handle = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const available = allQuestions
    .filter((q) => isActiveStatus(q) && q.id !== currentQuestionId && !value.includes(q.id) &&
      (q.displayQuestion || q.questionTemplate || '').toLowerCase().includes(search.toLowerCase()))
    .slice(0, 30);

  const selected = allQuestions.filter((q) => value.includes(q.id));

  return (
    <div className="admin-recq" ref={wrapRef}>
      <div className={`admin-recq__box ${open ? 'is-open' : ''}`} onClick={() => setOpen((v) => !v)}>
        {selected.length === 0 && <span className="admin-recq__placeholder">Select one or more related questions</span>}
        {selected.map((q) => (
          <span key={q.id} className="admin-recq__tag">
            <span>{q.displayQuestion || q.questionTemplate}</span>
            <button type="button" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onChange(value.filter((id) => id !== q.id)); }}>×</button>
          </span>
        ))}
        <svg viewBox="0 0 24 24" width="14" height="14" className="admin-recq__arrow"><path fill="currentColor" d="M7 10l5 5 5-5H7z" /></svg>
      </div>
      {open && (
        <div className="admin-recq__dropdown">
          <input
            className="admin-recq__search"
            type="text"
            placeholder="Search questions…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
          <ul>
            {available.length === 0
              ? <li className="admin-recq__empty">No questions found</li>
              : available.map((q) => (
                <li key={q.id} onMouseDown={(e) => { e.preventDefault(); onChange([...value, q.id]); setSearch(''); }}>
                  {q.displayQuestion || q.questionTemplate}
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ResponseFieldsInput({ value, onChange, columns, loading }) {
  const [inputVal, setInputVal] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const close = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const available = columns.filter((column) =>
    !value.includes(column.value) &&
    (!inputVal || column.value.toLowerCase().includes(inputVal.toLowerCase()) || column.name.toLowerCase().includes(inputVal.toLowerCase()))
  );

  const add = (column) => {
    if (column && !value.includes(column.value)) onChange([...value, column.value]);
    setInputVal('');
    inputRef.current?.focus();
  };

  return (
    <div className="admin-rf-wrap" ref={wrapRef}>
      <div className="admin-rf-box" onClick={() => { setOpen(true); inputRef.current?.focus(); }}>
        {value.map((v) => (
          <span key={v} className="admin-rf-tag">
            {v}
            <button type="button" onClick={() => onChange(value.filter((f) => f !== v))}>×</button>
          </span>
        ))}
        <input
          ref={inputRef}
          className="admin-rf-input"
          placeholder={loading ? 'Loading columns…' : value.length === 0 ? 'Search and select columns…' : 'Add another column…'}
          value={inputVal}
          disabled={loading || columns.length === 0}
          onChange={(e) => { setInputVal(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && available.length) { e.preventDefault(); add(available[0]); }
            if (e.key === 'Backspace' && !inputVal && value.length > 0) onChange(value.slice(0, -1));
            if (e.key === 'Escape') setOpen(false);
          }}
        />
        <svg viewBox="0 0 24 24" width="14" height="14" className="admin-rf-arrow"><path fill="currentColor" d="M7 10l5 5 5-5H7z" /></svg>
      </div>
      {open && !loading && (
        <ul className="admin-rf-suggest">
          {available.length === 0
            ? <li className="admin-rf-suggest__empty">{columns.length ? 'No columns found' : 'No columns available for this platform'}</li>
            : available.slice(0, 50).map((column) => (
              <li key={column.value} onMouseDown={(event) => { event.preventDefault(); add(column); }}>
                <span className="admin-rf-suggest__value">{column.value}</span>
                <span className="admin-rf-suggest__name">{column.name}</span>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

function ColumnSelect({ value, columns, loading, onChange }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    const handle = (e) => {
      if (
        wrapRef.current && !wrapRef.current.contains(e.target) &&
        !(dropRef.current && dropRef.current.contains(e.target))
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const handleOpen = () => setOpen((v) => !v);

  const filtered = columns.filter((c) =>
    !search ||
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.value.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="admin-col-select" ref={wrapRef}>
      <button type="button" className="admin-param-name-cell admin-col-select__trigger" onClick={handleOpen}>
        <span>{value || 'Select column'}</span>
        <svg viewBox="0 0 24 24" width="12" height="12"><path fill="currentColor" d="M7 10l5 5 5-5H7z" /></svg>
      </button>
      {open && (
        <div className="admin-col-select__dropdown" ref={dropRef}>
          <input
            className="admin-col-select__search"
            type="text"
            placeholder="Search columns…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
          <ul>
            {loading && <li className="admin-col-select__empty">Loading columns…</li>}
            {!loading && filtered.length === 0 && <li className="admin-col-select__empty">{columns.length === 0 ? 'Select a platform first' : 'No columns found'}</li>}
            {!loading && filtered.map((c) => (
              <li key={c.value} onMouseDown={(e) => { e.preventDefault(); onChange(c.value); setOpen(false); setSearch(''); }}>
                <span className="admin-col-select__val">{c.value}</span>
                <span className="admin-col-select__name">{c.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function DefaultValueAutocomplete({ value, platform, column, onChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    const handle = (e) => {
      if (
        wrapRef.current && !wrapRef.current.contains(e.target) &&
        !(dropRef.current && dropRef.current.contains(e.target))
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!value || value.length < 2 || !column || !platform) { setSuggestions([]); setOpen(false); return; }
      const results = await getPlatformSuggestions(platform, column, value);
      setSuggestions(results);
      if (results.length > 0) setOpen(true); else setOpen(false);
      setActiveIndex(-1);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, platform, column]);

  const pick = (s) => { onChange(s); setSuggestions([]); setOpen(false); setActiveIndex(-1); };

  const handleKeyDown = (e) => {
    if (!open || !suggestions.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => (i + 1) % suggestions.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1)); }
    else if (e.key === 'Enter' && activeIndex >= 0) { e.preventDefault(); pick(suggestions[activeIndex]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className="admin-param-val-wrap" ref={wrapRef}>
      <input
        className="admin-param-val-input"
        placeholder={`Enter value${column ? ` for ${column}` : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        autoComplete="off"
      />
      {value && (
        <button type="button" className="admin-param-clear" onClick={() => { onChange(''); setSuggestions([]); setOpen(false); }}>×</button>
      )}
      <svg viewBox="0 0 24 24" width="13" height="13" style={{ flexShrink: 0, color: '#94A3B8' }}><path fill="currentColor" d="M7 10l5 5 5-5H7z" /></svg>
      {open && suggestions.length > 0 && (
        <ul className="admin-param-suggest" ref={dropRef}>
          {suggestions.map((s, i) => (
            <li
              key={i}
              className={i === activeIndex ? 'is-active' : ''}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
            >{s}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function QuestionsPage() {
  const [view, setView] = useState('list');
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [platFilter, setPlatFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pageFlash, setPageFlash] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewDetail, setPreviewDetail] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErr, setFormErr] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [queryConfigQuestion, setQueryConfigQuestion] = useState(null);
  const [queryConfig, setQueryConfig] = useState({ searchFields: [], responseFields: [], limit: '1', responseType: 'single' });
  const [queryConfigLoading, setQueryConfigLoading] = useState(false);
  const [queryConfigSaving, setQueryConfigSaving] = useState(false);
  const [platformColumns, setPlatformColumns] = useState([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [addParameterOpen, setAddParameterOpen] = useState(false);
  const [pendingPlatform, setPendingPlatform] = useState(null);

  const debKeyword = useDebounce(keyword, 350);

  useEffect(() => {
    if (!form.platform) { setPlatformColumns([]); return; }
    setColumnsLoading(true);
    getPlatformColumns(form.platform)
      .then((cols) => setPlatformColumns(cols))
      .finally(() => setColumnsLoading(false));
  }, [form.platform]);

  const flash = (type, msg) => {
    setPageFlash({ type, msg });
    setTimeout(() => setPageFlash(null), 4000);
  };

  const CurrentPageAlert = () => pageFlash && (
    <div className={`admin-page-alert admin-page-alert--${pageFlash.type}`} role="alert">
      {pageFlash.type === 'success'
        ? <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 0 1 0 1.414l-8 8a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 1.414-1.414L8 12.586l7.293-7.293a1 1 0 0 1 1.414 0z" clipRule="evenodd" /></svg>
        : <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5zm0 10a1 1 0 1 0 0-2 1 1 0 0 2z" clipRule="evenodd" /></svg>}
      <span>{pageFlash.msg}</span>
    </div>
  );

  useEffect(() => {
    getAdminCategories({ limit: 200 }).then((r) => setCategories(r.categories || [])).catch(() => {});
    getAdminQuestions({ limit: 200 }).then((r) => setAllQuestions(r.questions || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: pageSize };
    if (debKeyword) params.keyword = debKeyword;
    if (catFilter) params.categoryId = catFilter;
    if (platFilter) params.platform = platFilter;
    if (statusFilter !== '') params.status = statusFilter;
    getAdminQuestions(params)
      .then((res) => { setQuestions(res.questions); setTotal(res.total); setTotalPages(res.totalPages); setPageSize(res.pageSize || 10); })
      .catch((err) => flash('error', err.message))
      .finally(() => setLoading(false));
  }, [page, pageSize, debKeyword, catFilter, platFilter, statusFilter]);

  useEffect(() => { if (view === 'list') load(); }, [load, view]);
  useEffect(() => { setPage(1); }, [debKeyword, catFilter, platFilter, statusFilter, pageSize]);

  const detectedParams = extractParams(form.questionTemplate);

  const paramRows = [
    ...detectedParams.map((k) => ({ key: k })),
  ];

  const setField = (key) => (val) => {
    setForm((f) => {
      const updated = { ...f, [key]: val };
      if (key === 'questionTemplate') {
        const params = extractParams(val);
        const newDefaults = {};
        const newDbFieldMap = {};
        params.forEach((p) => { newDefaults[p] = f.defaultParameters[p] ?? ''; });
        params.forEach((p) => { newDbFieldMap[p] = f.dbFieldMap?.[p] ?? ''; });
        return { ...updated, defaultParameters: newDefaults, dbFieldMap: newDbFieldMap, editableFields: params };
      }
      if (key === 'platform') return { ...updated, dbFieldMap: {} };
      return updated;
    });
    if (formErr[key]) setFormErr((fe) => { const n = { ...fe }; delete n[key]; return n; });
  };

  const hasPlatformSpecificData = Object.values(form.defaultParameters).some(Boolean)
    || Object.values(form.dbFieldMap).some(Boolean);

  const changePlatform = (platform) => {
    if (platform === form.platform) return;
    if (form.platform && hasPlatformSpecificData) {
      setPendingPlatform(platform);
      return;
    }
    setField('platform')(platform);
  };

  const confirmPlatformChange = () => {
    setForm((current) => {
      const parameters = extractParams(current.questionTemplate);
      return {
        ...current,
        platform: pendingPlatform,
        defaultParameters: Object.fromEntries(parameters.map((parameter) => [parameter, ''])),
        dbFieldMap: {},
        editableFields: parameters,
      };
    });
    setFormErr((errors) => {
      const next = { ...errors };
      delete next.dbFieldMap;
      delete next.defaultParameters;
      return next;
    });
    setPendingPlatform(null);
  };

  const addParameter = (parameter) => {
    setForm((current) => {
      const spacer = current.questionTemplate.trim() ? ' ' : '';
      return {
        ...current,
        questionTemplate: `${current.questionTemplate}${spacer}{${parameter}}`,
        defaultParameters: { ...current.defaultParameters, [parameter]: '' },
        dbFieldMap: { ...current.dbFieldMap, [parameter]: '' },
        editableFields: [...current.editableFields, parameter],
      };
    });
    setAddParameterOpen(false);
  };

  const removeParameter = (parameter) => {
    setForm((current) => {
      const placeholder = new RegExp(`\\{${parameter}\\}`, 'g');
      const questionTemplate = current.questionTemplate.replace(placeholder, '').replace(/\s{2,}/g, ' ').trim();
      const { [parameter]: ignoredDefault, ...defaultParameters } = current.defaultParameters;
      const { [parameter]: ignoredMapping, ...dbFieldMap } = current.dbFieldMap;
      return {
        ...current,
        questionTemplate,
        defaultParameters,
        dbFieldMap,
        editableFields: current.editableFields.filter((field) => field !== parameter),
      };
    });
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormErr({});
    setEditingQuestion(null);
    setView('form');
  };

  const openEdit = async (q) => {
    setDetailLoading(true);
    setView('form');
    try {
      const detail = await getAdminQuestion(q.id);
      setForm({
        categoryId: detail.categoryId || '',
        questionTemplate: detail.questionTemplate || '',
        platform: detail.platform || '',
        displayOrder: detail.displayOrder != null ? String(detail.displayOrder) : '1',
        isRecommended: !!detail.isRecommended,
        defaultParameters: detail.defaultParameters || {},
        editableFields: detail.editableFields || [],
        dbFieldMap: detail.dbFieldMap || {},
        recommendedQuestionIds: detail.recommendedQuestionIds || [],
      });
      setFormErr({});
      setEditingQuestion(detail);
    } catch (err) {
      flash('error', err.message);
      setView('list');
    } finally {
      setDetailLoading(false);
    }
  };

  const openQueryConfig = async (q) => {
    setQueryConfigQuestion(q);
    setQueryConfigLoading(true);
    setView('query-config');
    setColumnsLoading(true);
    getPlatformColumns(q.platform)
      .then((columns) => setPlatformColumns(columns))
      .catch(() => setPlatformColumns([]))
      .finally(() => setColumnsLoading(false));
    const params = Object.keys(q.defaultParameters || {});
    const fieldMap = q.dbFieldMap || {};
    try {
      const config = await getQueryConfig(q.id);
      setQueryConfig({
        searchFields: config?.searchFields?.length
          ? config.searchFields
          : params.map((p) => ({ parameter: p, field: fieldMap[p] || p, operator: '=', required: true })),
        responseFields: config?.responseFields?.length ? config.responseFields : params,
        responseType: config?.responseType === 'multiple' ? 'multiple' : 'single',
        limit: String(config?.limit ?? (config?.responseType === 'multiple' ? 10 : 1)),
      });
    } catch {
      setQueryConfig({
        searchFields: params.map((p) => ({ parameter: p, field: fieldMap[p] || p, operator: '=', required: true })),
        responseFields: params,
        responseType: 'single',
        limit: '1',
      });
    } finally {
      setQueryConfigLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) { setFormErr(errs); return; }
    setSubmitting(true);
    setFormErr({});
    const cleanDefaults = Object.fromEntries(
      Object.entries(form.defaultParameters).filter(([k]) => !k.startsWith('__CUSTOM_'))
    );
    const body = {
      categoryId: form.categoryId,
      questionTemplate: form.questionTemplate.trim(),
      platform: form.platform,
      isRecommended: form.isRecommended,
      defaultParameters: cleanDefaults,
      editableFields: detectedParams,
      dbFieldMap: Object.fromEntries(detectedParams.map((parameter) => [parameter, form.dbFieldMap?.[parameter]])),
      recommendedQuestionIds: form.recommendedQuestionIds,
      ...(form.displayOrder !== '' ? { displayOrder: Number(form.displayOrder) } : {}),
    };
    try {
      if (!editingQuestion) {
        await createQuestion(body);
        flash('success', 'Question created. Inactive by default — activate when ready.');
      } else {
        await updateQuestion(editingQuestion.id, body);
        flash('success', 'Question updated successfully.');
      }
      setView('list');
    } catch (err) {
      setFormErr({ _api: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (q) => {
    if (toggling) return;
    const newStatus = !(q.status === true || q.status === 'active');
    setToggling(q.id);
    try {
      await patchQuestionStatus(q.id, newStatus);
      flash('success', `Question ${newStatus ? 'activated' : 'deactivated'}.`);
      load();
    } catch (err) {
      flash('error', err.message);
    } finally {
      setToggling(null);
    }
  };

  const handleDelete = async (question) => {
    if (isActiveStatus(question) || deleting) return;
    setDeleting(question.id);
    try {
      await deleteQuestion(question.id);
      flash('success', 'Question deleted successfully.');
      if (questions.length === 1 && page > 1) setPage((current) => current - 1);
      else load();
    } catch (err) {
      flash('error', err.message);
    } finally {
      setDeleting(null);
      setDeleteTarget(null);
    }
  };

  const openPreview = (q) => {
    setPreview({ ...q });
    setPreviewDetail(null);
    setPreviewResult(null);
    setPreviewLoading(true);
    previewQuestion(q.id, q.defaultParameters || {})
      .then((res) => setPreviewResult({ ok: true, ...res }))
      .catch((err) => setPreviewResult({ ok: false, message: err.message }))
      .finally(() => setPreviewLoading(false));
  };

  const handleQueryConfigSave = async () => {
    if (!queryConfigQuestion) return;
    if (!Number.isInteger(Number(queryConfig.limit)) || Number(queryConfig.limit) < 1) {
      flash('error', 'Result limit must be at least 1.');
      return;
    }
    setQueryConfigSaving(true);
    try {
      await updateQueryConfig(queryConfigQuestion.id, {
        searchFields: queryConfig.searchFields,
        responseFields: queryConfig.responseFields,
        responseType: queryConfig.responseType,
        limit: Number(queryConfig.limit),
      });
      flash('success', 'Query configuration saved.');
      setView('list');
    } catch (err) {
      flash('error', err.message);
    } finally {
      setQueryConfigSaving(false);
    }
  };

  const isActive = isActiveStatus;
  const activeCategories = categories.filter(isActiveStatus);
  const catName = (id) => categories.find((c) => c.id === id)?.categoryName || id;

  /* ── Query Configuration Page ── */
  if (view === 'query-config') {
    return (
      <div className="admin-page">
        <div className="admin-page__head">
          <div>
            <button type="button" className="admin-page__back" onClick={() => setView('list')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="17" height="17"><path d="m15 18-6-6 6-6" /></svg>
              Back to Questions
            </button>
            <h1 className="admin-page__title">Query Configuration</h1>
            <p className="admin-page__sub">Configure search fields, operators and response fields for this question.</p>
          </div>
        </div>

        {queryConfigLoading ? (
          <div className="admin-loading"><span className="admin-spinner admin-spinner--dark" />Loading configuration…</div>
        ) : (
          <>
            <div className="admin-panel admin-qc-section">
              <div className="admin-qc-section__head">
                <span className="admin-qc-num">1</span>
                <div>
                  <h3 className="admin-qc-section__title">Search Fields</h3>
                  <p className="admin-qc-section__sub">Define fields and operators used for searching data.</p>
                </div>
              </div>
              <div style={{ padding: '0 20px 20px' }}>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Search Field <span style={{ color: 'var(--a-danger)' }}>*</span></th>
                        <th>Operator <span style={{ color: 'var(--a-danger)' }}>*</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {queryConfig.searchFields.map((sf, i) => (
                        <tr key={i}>
                          <td>
                            <input className="admin-form-input admin-sf-field-input" value={sf.parameter || '—'} readOnly />
                            <div className="admin-form-hint" style={{ marginTop: 4 }}>Parameter is auto selected from question configuration</div>
                          </td>
                          <td>
                            <input className="admin-form-input admin-sf-field-input" value={sf.field} readOnly />
                            <div className="admin-form-hint" style={{ marginTop: 4 }}>Field name is auto selected from DB mapping</div>
                          </td>
                          <td>
                            <select
                              className="admin-form-select"
                              value={sf.operator}
                              onChange={(e) => {
                                const updated = queryConfig.searchFields.map((s, j) => j === i ? { ...s, operator: e.target.value } : s);
                                setQueryConfig((c) => ({ ...c, searchFields: updated }));
                              }}
                            >
                              {OPERATORS.map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
                            </select>
                          </td>
                        </tr>
                      ))}
                      {queryConfig.searchFields.length === 0 && (
                        <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--a-muted)', padding: 20, fontSize: 13 }}>No search fields configured</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="admin-qc-note">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9z" clipRule="evenodd" /></svg>
                  Search fields are automatically added based on question parameters and cannot be changed.
                </div>
              </div>
            </div>

            <div className="admin-panel admin-qc-section">
              <div className="admin-qc-section__head">
                <span className="admin-qc-num">2</span>
                <div>
                  <h3 className="admin-qc-section__title">Response Fields</h3>
                  <p className="admin-qc-section__sub">Select the fields/columns you want to display in the query response.</p>
                </div>
              </div>
              <div style={{ padding: '0 20px 20px' }}>
                <div className="admin-form-group">
                  <label className="admin-form-label">Response Fields <span style={{ color: 'var(--a-danger)' }}>*</span></label>
                  <ResponseFieldsInput
                    value={queryConfig.responseFields}
                    onChange={(fields) => setQueryConfig((c) => ({ ...c, responseFields: fields }))}
                    columns={platformColumns}
                    loading={columnsLoading}
                  />
                  <div className="admin-form-hint">Select one or more columns to include in the response.</div>
                </div>
                <div className="admin-form-row admin-qc-response-settings">
                  <div className="admin-form-group" style={{ marginBottom: 0 }}>
                    <label className="admin-form-label" htmlFor="qc-response-type">Response Type <span style={{ color: 'var(--a-danger)' }}>*</span></label>
                    <select
                      id="qc-response-type"
                      className="admin-form-select"
                      value={queryConfig.responseType}
                      onChange={(event) => {
                        const responseType = event.target.value;
                        setQueryConfig((config) => ({
                          ...config,
                          responseType,
                          limit: responseType === 'single' ? '1' : (config.limit === '1' ? '10' : config.limit),
                        }));
                      }}
                    >
                      <option value="single">Single result</option>
                      <option value="multiple">Multiple results</option>
                    </select>
                    <div className="admin-form-hint">Choose whether users receive one result or a result list.</div>
                  </div>
                  <div className="admin-form-group" style={{ marginBottom: 0 }}>
                    <label className="admin-form-label" htmlFor="qc-limit">Result Limit <span style={{ color: 'var(--a-danger)' }}>*</span></label>
                    <input
                      id="qc-limit"
                      type="number"
                      min="1"
                      step="1"
                      className="admin-form-input"
                      value={queryConfig.limit}
                      disabled={queryConfig.responseType === 'single'}
                      onChange={(event) => setQueryConfig((config) => ({ ...config, limit: event.target.value.replace(/[^0-9]/g, '') }))}
                    />
                    <div className="admin-form-hint">{queryConfig.responseType === 'single' ? 'Single result always uses a limit of 1.' : 'Maximum number of records to return.'}</div>
                  </div>
                </div>
                <div className="admin-qc-tip">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" style={{ color: '#16A34A', flexShrink: 0 }}><path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5z" clipRule="evenodd" /></svg>
                  <span><strong>Tip:</strong> Choose only the required fields to optimize performance and response size.</span>
                </div>
              </div>
            </div>

            <div className="admin-qc-bottom-note">
              <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9z" clipRule="evenodd" /></svg>
              <strong>Note:</strong> This configuration will be used by the query engine to fetch and display results.
            </div>
            <CurrentPageAlert />
            <div className="admin-modal__foot admin-page__form-actions">
              <button type="button" className="admin-btn admin-btn--ghost" onClick={() => setView('list')}>Cancel</button>
              <button type="button" className="admin-btn admin-btn--primary" onClick={handleQueryConfigSave} disabled={queryConfigSaving || !Number.isInteger(Number(queryConfig.limit)) || Number(queryConfig.limit) < 1}>
                {queryConfigSaving && <span className="admin-spinner" />}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><path d="M17 21v-8H7v8M7 3v5h8" /></svg>
                {queryConfigSaving ? 'Saving…' : 'Save Configuration'}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  /* ── Create / Edit Form Page ── */
  if (view === 'form') {
    const templateLen = form.questionTemplate.length;
    return (
      <div className="admin-page">
        {detailLoading ? (
          <div className="admin-loading"><span className="admin-spinner admin-spinner--dark" />Loading question…</div>
        ) : (
          <>
            <div className="admin-page__head">
              <div>
                <button type="button" className="admin-page__back" onClick={() => setView('list')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="17" height="17"><path d="m15 18-6-6 6-6" /></svg>
                  Back to Questions
                </button>
                <h1 className="admin-page__title">{editingQuestion ? 'Edit Question' : 'Create Question'}</h1>
                <p className="admin-page__sub">{editingQuestion ? 'Update the question configuration.' : 'Add a new question with parameters and configuration.'}</p>
              </div>
            </div>

            <CurrentPageAlert />

            <div className="admin-panel">
              <form onSubmit={handleSubmit} noValidate>
                <div className="admin-qform-body">
                  {formErr._api && (
                    <div className="admin-alert admin-alert--error">
                      <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" clipRule="evenodd" /></svg>
                      {formErr._api}
                    </div>
                  )}

                  {/* Category + Platform */}
                  <div className="admin-form-row" style={{ marginBottom: 20 }}>
                    <div className="admin-form-group" style={{ marginBottom: 0 }}>
                      <label className="admin-form-label">Category <span>*</span></label>
                      <SearchSelect
                        value={form.categoryId}
                        onChange={setField('categoryId')}
                        placeholder="Select Category"
                        options={activeCategories.map((c) => ({ value: c.id, label: c.categoryName }))}
                        error={!!formErr.categoryId}
                      />
                      {formErr.categoryId && <div className="admin-form-error">{formErr.categoryId}</div>}
                    </div>
                    <div className="admin-form-group" style={{ marginBottom: 0 }}>
                      <label className="admin-form-label">Platform <span>*</span></label>
                      <SearchSelect
                        value={form.platform}
                        onChange={changePlatform}
                        placeholder="Select Platform"
                        options={PLATFORMS.map((p) => ({ value: p, label: p }))}
                        error={!!formErr.platform}
                      />
                      {formErr.platform && <div className="admin-form-error">{formErr.platform}</div>}
                    </div>
                  </div>

                  {/* Question Template */}
                  <div className="admin-form-group" style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <label className="admin-form-label" style={{ marginBottom: 0 }} htmlFor="q-tmpl">Question Template <span>*</span></label>
                      <span className={`admin-char-count${templateLen > MAX_TEMPLATE_LEN * 0.9 ? ' is-warn' : ''}`}>{templateLen}/{MAX_TEMPLATE_LEN}</span>
                    </div>
                    <textarea
                      id="q-tmpl"
                      className={`admin-form-textarea${formErr.questionTemplate ? ' is-error' : ''}`}
                      placeholder="Enter question template"
                      value={form.questionTemplate}
                      onChange={(e) => { if (e.target.value.length <= MAX_TEMPLATE_LEN) setField('questionTemplate')(e.target.value); }}
                      rows={3}
                    />
                    {formErr.questionTemplate && <div className="admin-form-error">{formErr.questionTemplate}</div>}
                  </div>

                  {/* Info box */}
                  <div className="admin-info-box" style={{ marginBottom: 20 }}>
                    <div className="admin-info-box__note">
                      <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" style={{ flexShrink: 0, color: 'var(--a-info)' }}><path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9z" clipRule="evenodd" /></svg>
                      <div>
                        <strong>Note:</strong> Question-template parameters and default parameters must be the same.<br />
                        Use <code style={{ background: '#DBEAFE', padding: '1px 5px', borderRadius: 3 }}>{'{COLUMN_NAME}'}</code> format to define parameters in the question template.
                      </div>
                    </div>
                    <div className="admin-info-box__example">
                      <div className="admin-info-box__row">
                        <span className="admin-info-box__key">Question Template:</span>
                        <code className="admin-info-box__code">Show competitors for <span style={{ color: '#4F46E5' }}>{'{PRODUCT}'}</span> in <span style={{ color: '#4F46E5' }}>{'{COUNTRY}'}</span></code>
                      </div>
                      <div className="admin-info-box__row">
                        <span className="admin-info-box__key">Default Parameters:</span>
                        <span>
                          <span className="admin-info-box__param">PRODUCT = ACTIVE_INGREDIENT = Mesotrione</span>
                          <span style={{ margin: '0 6px', color: '#94A3B8' }}>—</span>
                          <span className="admin-info-box__param">COUNTRY = COUNTRY_NAME = India</span>
                        </span>
                      </div>
                      <div className="admin-info-box__row">
                        <span className="admin-info-box__key">Result:</span>
                        <span style={{ color: '#16A34A', fontWeight: 500 }}>Show competitors for Mesotrione in India</span>
                      </div>
                    </div>
                  </div>

                  {/* Parameter to database-field mapping */}
                  <div className="admin-form-group" style={{ marginBottom: 20 }}>
                    <label className="admin-form-label">Default Parameters &amp; Database Mapping <span>*</span></label>
                    <div className="admin-params-wrap">
                      <table className="admin-params-table">
                        <thead>
                          <tr>
                            <th>Business Parameter</th>
                            <th>Select Column (DB Field)</th>
                            <th>Default Value</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paramRows.length === 0 ? (
                            <tr>
                              <td colSpan={4} style={{ textAlign: 'center', color: 'var(--a-muted)', padding: '20px 16px', fontSize: 13 }}>
                                No parameters detected. Use {'{PARAM_NAME}'} in your template above.
                              </td>
                            </tr>
                          ) : paramRows.map(({ key }) => (
                            <tr key={key}>
                              <td>
                                <div className="admin-param-name-cell"><span>{key}</span></div>
                              </td>
                              <td>
                                <ColumnSelect
                                  value={form.dbFieldMap?.[key] || ''}
                                  columns={platformColumns}
                                  loading={columnsLoading}
                                  onChange={(column) => setForm((f) => ({
                                    ...f,
                                    dbFieldMap: { ...f.dbFieldMap, [key]: column },
                                    defaultParameters: { ...f.defaultParameters, [key]: '' },
                                  }))}
                                />
                              </td>
                              <td>
                                <DefaultValueAutocomplete
                                  value={form.defaultParameters[key] || ''}
                                  platform={form.platform}
                                  column={form.dbFieldMap?.[key] || ''}
                                  onChange={(val) => setForm((f) => ({ ...f, defaultParameters: { ...f.defaultParameters, [key]: val } }))}
                                />
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="admin-btn admin-btn--ghost admin-btn--sm admin-btn--icon-danger"
                                  onClick={() => removeParameter(key)}
                                  title={`Remove {${key}} from the question template`}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" /></svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {formErr.dbFieldMap && <div className="admin-form-error">{formErr.dbFieldMap}</div>}
                    {formErr.defaultParameters && <div className="admin-form-error">{formErr.defaultParameters}</div>}
                    <button
                      type="button"
                      className="admin-add-param-btn"
                      onClick={() => setAddParameterOpen(true)}
                      disabled={!form.platform}
                      title={form.platform ? 'Add a parameter to the question template' : 'Select a platform first'}
                    >
                      <span>+</span> Add Parameter
                    </button>
                    {!form.platform && <div className="admin-form-hint">Select a platform before adding a parameter.</div>}
                  </div>

                  {/* Display Order + Home Featured + Recommended Questions */}
                  <div className="admin-form-row admin-form-row--3" style={{ marginBottom: 20 }}>
                    <div className="admin-form-group" style={{ marginBottom: 0 }}>
                      <label className="admin-form-label" htmlFor="q-order">Display Order <span>*</span></label>
                      <select id="q-order" className="admin-form-select" value={form.displayOrder} onChange={(e) => setField('displayOrder')(e.target.value)}>
                        {Array.from({ length: 100 }, (_, i) => i + 1).map((n) => <option key={n} value={String(n)}>{n}</option>)}
                      </select>
                      <div className="admin-form-hint">Select Question Order (1 to 100)</div>
                    </div>
                    <div className="admin-form-group" style={{ marginBottom: 0 }}>
                      <label className="admin-form-label">Home Featured</label>
                      <div style={{ paddingTop: 8 }}>
                        <Toggle checked={form.isRecommended} onChange={(v) => setField('isRecommended')(v)} />
                      </div>
                    </div>
                    <div className="admin-form-group" style={{ marginBottom: 0 }}>
                      <label className="admin-form-label">Recommended Questions</label>
                      <RecommendedQuestionsSelect
                        value={form.recommendedQuestionIds}
                        onChange={(ids) => setField('recommendedQuestionIds')(ids)}
                        allQuestions={allQuestions}
                        currentQuestionId={editingQuestion?.id}
                      />
                      <div className="admin-form-hint">Select one or more related questions</div>
                    </div>
                  </div>

                  {/* Tip box */}
                  <div className="admin-tip-box">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" style={{ color: '#F59E0B', flexShrink: 0 }}><path d="M11 3a1 1 0 1 0-2 0v1a1 1 0 1 0 2 0V3zM15.657 5.757a1 1 0 0 0-1.414-1.414l-.707.707a1 1 0 0 0 1.414 1.414l.707-.707zM18 10a1 1 0 0 1-1 1h-1a1 1 0 1 1 0-2h1a1 1 0 0 1 1 1zM5.05 6.464A1 1 0 0 0 6.464 5.05l-.707-.707a1 1 0 0 0-1.414 1.414l.707.707zM5 10a1 1 0 0 1-1 1H3a1 1 0 1 1 0-2h1a1 1 0 0 1 1 1zM8 16v-1h4v1a2 2 0 1 1-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 1 0-4.954 0c.27.213.462.519.476.859h4.001z" /></svg>
                    <span><strong>Tip:</strong> Recommended questions will be shown to users along with this question in the application.</span>
                  </div>
                </div>

                <div className="admin-modal__foot">
                  <button type="button" className="admin-btn admin-btn--ghost" onClick={() => setView('list')}>Cancel</button>
                  <button type="submit" className="admin-btn admin-btn--primary" disabled={submitting}>
                    {submitting && <span className="admin-spinner" />}
                    {submitting ? 'Saving…' : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><path d="M17 21v-8H7v8M7 3v5h8" /></svg>
                        {editingQuestion ? 'Save Changes' : 'Save Question'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {addParameterOpen && (
              <AddParameterModal
                existingParameters={detectedParams}
                onClose={() => setAddParameterOpen(false)}
                onAdd={addParameter}
              />
            )}

            {pendingPlatform && (
              <div className="admin-confirm" role="dialog" aria-modal="true" aria-labelledby="platform-change-title">
                <div className="admin-confirm__backdrop" onClick={() => setPendingPlatform(null)} />
                <div className="admin-confirm__box admin-platform-change">
                  <div className="admin-confirm__icon">!</div>
                  <h2 id="platform-change-title">Change platform?</h2>
                  <p>Your selected DB fields and default values will be cleared because each platform has different columns. Your category and question template will remain unchanged.</p>
                  <div className="admin-confirm__actions">
                    <button type="button" className="admin-btn admin-btn--ghost" onClick={() => setPendingPlatform(null)}>Keep current platform</button>
                    <button type="button" className="admin-btn admin-btn--danger" onClick={confirmPlatformChange}>Change platform</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  /* ── Questions List ── */
  return (
    <div className="admin-page">
      <div className="admin-page__head">
        <div>
          <h1 className="admin-page__title">Questions</h1>
          <p className="admin-page__sub">Create and manage chatbot questions</p>
        </div>
        <button type="button" className="admin-btn admin-btn--primary" onClick={openCreate}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><path d="M12 5v14M5 12h14" /></svg>
          New Question
        </button>
      </div>

      <div className="admin-panel">
        {pageFlash && (
          <div className={`admin-page-alert admin-page-alert--${pageFlash.type}`}>
            {pageFlash.type === 'success'
              ? <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 0 1 0 1.414l-8 8a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 1.414-1.414L8 12.586l7.293-7.293a1 1 0 0 1 1.414 0z" clipRule="evenodd" /></svg>
              : <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" clipRule="evenodd" /></svg>}
            {pageFlash.msg}
          </div>
        )}

        <div className="admin-toolbar">
          <div className="admin-toolbar__search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
            <input type="text" placeholder="Search questions…" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
          </div>
          <SearchSelect
            value={catFilter}
            onChange={setCatFilter}
            placeholder="All Categories"
            options={[{ value: '', label: 'All Categories' }, ...categories.map((c) => ({ value: c.id, label: c.categoryName }))]}
          />
          <SearchSelect
            value={platFilter}
            onChange={setPlatFilter}
            placeholder="All Platforms"
            options={[{ value: '', label: 'All Platforms' }, ...PLATFORMS.map((p) => ({ value: p, label: p }))]}
          />
          <SearchSelect
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="All Status"
            options={[{ value: '', label: 'All Status' }, { value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }]}
          />
        </div>

        {loading || detailLoading ? (
          <div className="admin-loading"><span className="admin-spinner admin-spinner--dark" />Loading questions…</div>
        ) : questions.length === 0 ? (
          <div className="admin-empty">
            <div className="admin-empty__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
            </div>
            <h3>No questions found</h3>
            <p>{keyword || catFilter || platFilter || statusFilter ? 'Try clearing your filters.' : 'Create your first question to get started.'}</p>
            {!keyword && !catFilter && !platFilter && !statusFilter && (
              <button type="button" className="admin-btn admin-btn--primary" onClick={openCreate}>Create Question</button>
            )}
          </div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Question / Template</th>
                  <th>Category</th>
                  <th>Platform</th>
                  <th>Featured</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {questions.map((q, i) => (
                  <tr key={q.id}>
                    <td style={{ color: 'var(--a-muted)', fontWeight: 500 }}>{(page - 1) * pageSize + i + 1}</td>
                    <td className="admin-table__q">
                      <div style={{ fontWeight: 500, marginBottom: 4 }}>{q.displayQuestion || q.questionTemplate}</div>
                      {q.questionTemplate && q.displayQuestion !== q.questionTemplate && (
                        <span className="admin-template-pill" title={q.questionTemplate}>{q.questionTemplate}</span>
                      )}
                    </td>
                    <td style={{ color: 'var(--a-muted)', fontSize: 12 }}>{catName(q.categoryId)}</td>
                    <td><span className="admin-mono">{q.platform || '—'}</span></td>
                    <td>
                      <span className={`admin-badge admin-badge--${q.isRecommended ? 'yes' : 'no'}`}>{q.isRecommended ? 'Yes' : 'No'}</span>
                    </td>
                    <td>
                      {toggling === q.id
                        ? <span className="admin-spinner admin-spinner--dark" style={{ width: 16, height: 16 }} />
                        : <Toggle checked={isActive(q)} onChange={() => handleToggle(q)} label={false} />}
                    </td>
                    <td>
                      <div className="admin-table__actions">
                        <button type="button" className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => openEdit(q)}>Edit</button>
                        <button type="button" className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => openQueryConfig(q)} title="Configure query">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                          Configure
                        </button>
                        <button type="button" className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => openPreview(q)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                          Preview
                        </button>
                        {!isActive(q) && (
                          <button
                            type="button"
                            className="admin-btn admin-btn--ghost admin-btn--sm admin-btn--icon-danger"
                            onClick={() => setDeleteTarget(q)}
                            disabled={deleting === q.id}
                            title="Delete inactive question"
                          >
                            {deleting === q.id ? <span className="admin-spinner admin-spinner--dark" /> : 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && total > 0 && (
          <div className="admin-pagination">
            <span className="admin-pagination__info">
              Showing <strong>{(page - 1) * pageSize + 1}</strong>–<strong>{Math.min(page * pageSize, total)}</strong> of <strong>{total}</strong> questions
            </span>
            <div className="admin-pagination__btns">
              <button className="admin-pagination__btn" onClick={() => setPage(1)} disabled={page <= 1} title="First page">«</button>
              <button className="admin-pagination__btn" onClick={() => setPage((p) => p - 1)} disabled={page <= 1} title="Previous page">‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && arr[idx - 1] !== p - 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) => p === '…'
                  ? <span key={`e${i}`} className="admin-pagination__ellipsis">…</span>
                  : <button key={p} className={`admin-pagination__btn${page === p ? ' is-active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                )}
              <button className="admin-pagination__btn" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages} title="Next page">›</button>
              <button className="admin-pagination__btn" onClick={() => setPage(totalPages)} disabled={page >= totalPages} title="Last page">»</button>
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {deleteTarget && (
        <DeleteConfirmModal
          itemType="question"
          itemLabel={deleteTarget.displayQuestion || deleteTarget.questionTemplate || 'this question'}
          busy={deleting === deleteTarget.id}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => handleDelete(deleteTarget)}
        />
      )}

      {preview && (
        <div className="admin-modal" role="dialog" aria-modal="true">
          <div className="admin-modal__backdrop" onClick={() => { setPreview(null); setPreviewDetail(null); }} />
          <div className="admin-modal__box admin-modal__box--wide admin-preview-modal">
            <div className="admin-modal__head">
              <h2>Preview Question</h2>
              <button type="button" className="admin-modal__close" onClick={() => { setPreview(null); setPreviewDetail(null); }}>×</button>
            </div>
            <div className="admin-modal__body">
              {previewLoading ? (
                <div className="admin-loading" style={{ padding: '32px 0' }}><span className="admin-spinner admin-spinner--dark" />Running preview…</div>
              ) : previewResult ? (
                previewResult.ok ? (
                  <div className="admin-end-preview">
                    <div className="admin-end-preview__label">What the end user will see</div>
                    <div className="admin-end-preview__question">
                      {previewResult.resolvedQuestion || preview.displayQuestion || preview.questionTemplate}
                    </div>
                    <div className="admin-end-preview__answer">
                      <span className="admin-end-preview__avatar">CR</span>
                      <div className="admin-end-preview__card">
                        <p className="admin-end-preview__count">{previewResult.count ?? 0} result{(previewResult.count ?? 0) === 1 ? '' : 's'} found.</p>
                        {previewRecords(previewResult).length > 0 ? (
                          previewRecords(previewResult).map((record, recordIndex) => (
                            <div className="admin-end-preview__table-wrap" key={recordIndex}>
                              {previewRecords(previewResult).length > 1 && <div className="admin-end-preview__record">Result {recordIndex + 1}</div>}
                              <table className="admin-end-preview__table">
                                <tbody>
                                  {(previewResult.columns || Object.keys(record).map((key) => ({ key, displayName: key }))).map((column) => {
                                    const key = typeof column === 'string' ? column : column.key;
                                    const label = typeof column === 'string' ? column.replace(/_/g, ' ') : (column.displayName || column.key.replace(/_/g, ' '));
                                    return (
                                      <tr key={key}>
                                        <th>{label}</th>
                                        <td><PreviewResultValue value={previewValue(record[key])} onViewDetails={(value) => setPreviewDetail({ label, value })} /></td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          ))
                        ) : (
                          <div className="admin-end-preview__empty">No matching records found for this question.</div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="admin-alert admin-alert--error" style={{ marginTop: 0 }}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" clipRule="evenodd" /></svg>
                    {previewResult.message}
                  </div>
                )
              ) : null}
            </div>
            <div className="admin-modal__foot">
              <button type="button" className="admin-btn admin-btn--ghost" onClick={() => { setPreview(null); setPreviewDetail(null); }}>Close</button>
              <button type="button" className="admin-btn admin-btn--primary" onClick={() => { setPreviewResult(null); setPreviewLoading(true); previewQuestion(preview.id, preview.defaultParameters || {}).then((res) => setPreviewResult({ ok: true, ...res })).catch((err) => setPreviewResult({ ok: false, message: err.message })).finally(() => setPreviewLoading(false)); }} disabled={previewLoading}>
                {previewLoading && <span className="admin-spinner" />}
                {previewLoading ? 'Running…' : 'Run Again'}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewDetail && (
        <div className="admin-preview-detail" role="dialog" aria-modal="true" aria-labelledby="preview-detail-title">
          <div className="admin-preview-detail__backdrop" onClick={() => setPreviewDetail(null)} />
          <div className="admin-preview-detail__box">
            <div className="admin-preview-detail__head">
              <div>
                <span>Full details</span>
                <h2 id="preview-detail-title">{previewDetail.label}</h2>
              </div>
              <button type="button" onClick={() => setPreviewDetail(null)} aria-label="Close full details">×</button>
            </div>
            <div className="admin-preview-detail__content">{previewDetail.value}</div>
            <div className="admin-preview-detail__foot">
              <button type="button" className="admin-btn admin-btn--primary" onClick={() => setPreviewDetail(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
